import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContent } from "react-toastify";
import { Row } from "@tanstack/react-table";
import { Teammember, Warehouse } from "types";
import Select, { SingleValue, components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import StatCard from "./ReportsCards";

import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
    createColumnHelper,
} from "@tanstack/react-table";

import {
    ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';

import { getAuthCookie } from "../utils/authCookies";

interface ExportRow {
    "First Name": string;
    "Last Name": string;
    "Email": string;
    "Phone": string;
    "Status": string;
}

function fuzzyTextFilter<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string
) {
    const raw = row.getValue(columnId);
    const value = (typeof raw === "boolean"
        ? raw
            ? "Active"
            : "Inactive"
        : String(raw)
    ).toLowerCase();

    return value.includes(filterValue.toLowerCase());
}

interface InstallerOption {
    label: string;
    value: string;
}

// Select2 types for installers
type InstallerOptionAsync = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
};

interface InstallerJobRow {
    installer_id: string;
    installer_name: string;
    jobs_completed: number;
    installations: number;
    removals: number;
    on_time_percent: string;
    installation_jobs_with_photos: number;
    last_job_date: string;
    cards: {
        active_installers: number;
        avg_on_time_percent: number;
        installations: number;
        removals: number;
        total_jobs: number;
        outstanding_balance: number;
        total_orders: number;
        total_revenue: number;
        amount_paid: number;
    };
}


const JOB_STATUS_OPTIONS = [
    { label: "Scheduled", value: "Scheduled" },
    { label: "In Progress", value: "In_Progress" },
    { label: "Completed", value: "Completed" },
    { label: "Canceled", value: "Canceled" },
];

const DATE_OPTIONS = [
    { label: "Last 7 Days", value: "last_7_days" },
    { label: "Last 15 Days", value: "last_15_days" },
    { label: "Last 30 Days", value: "last_30_days" },
    { label: "Custom", value: "custom" },
];

const columnHelper = createColumnHelper<InstallerJobRow>();

const DropdownIndicator = (props: any) => {
    if (props.selectProps.isDisabled) return null;
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
        </components.DropdownIndicator>
    );
};

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValue: (p: any, s: any) => ({ ...p, backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor }),
  multiValueLabel: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValueRemove: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : p.display }),
  // Fix dropdown menu positioning to appear below the input
  menu: (provided: any) => ({
    ...provided,
    zIndex: 9999,
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: "200px",
  }),
};

const JobComplitedReport = () => {
    const location = useLocation();
    const token = getAuthCookie("sessionToken");
    const organizationId = getAuthCookie("organizationId");
    
    const [data, setData] = useState<InstallerJobRow[]>([]);
    const [cards, setCards] = useState<InstallerJobRow["cards"]>({
        active_installers: 0,
        avg_on_time_percent: 0,
        installations: 0,
        removals: 0,
        total_jobs: 0,
        outstanding_balance: 0,
        total_orders: 0,
        total_revenue: 0,
        amount_paid: 0,
    });
    const [filtered, setFiltered] = useState<Teammember[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [warehouseFilter, setWarehouseFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [error, setError] = useState<string>("");
    // const [currentPage, setCurrentPage] = useState(1);
    const [rowCount, setRowCount] = useState(0);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [filteredTeammembers, setFilteredTeammembers] = useState<Teammember[]>([]);
    const [exportRows, setExportRows] = useState<ExportRow[]>([]);
    const [deleting, setDeleting] = useState<string | null>(null);
    const topRef = useRef<HTMLDivElement>(null);
    const [datePreset, setDatePreset] = useState(DATE_OPTIONS[2]); // default: Last 30 Days
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [selectedInstallers, setSelectedInstallers] = useState<string[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>(["Completed"]);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [globalFilter, setGlobalFilter] = useState("");
    const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "")
    
    // State for AsyncPaginate selected options
    const [selectedInstallerOptions, setSelectedInstallerOptions] = useState<InstallerOptionAsync[]>([]);
    
    // Calculate date range limits (last 6 months from today)
    const dateRangeLimits = useMemo(() => {
        const today = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        
        // Format as YYYY-MM-DD for date input
        const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            minDate: formatDate(sixMonthsAgo),
            maxDate: formatDate(today)
        };
    }, []);

    // Validate and reset dates when custom is selected or dates are out of range
    useEffect(() => {
        if (datePreset.value === "custom") {
            const minDate = dateRangeLimits.minDate;
            const maxDate = dateRangeLimits.maxDate;
            
            // Reset From date if it's outside the valid range
            if (customFrom && (customFrom < minDate || customFrom > maxDate)) {
                setCustomFrom("");
            }
            
            // Reset To date if it's outside the valid range
            if (customTo && (customTo < minDate || customTo > maxDate)) {
                setCustomTo("");
            }
        }
    }, [datePreset.value, dateRangeLimits, customFrom, customTo]);
    
    // Helper function and constants for AsyncPaginate
    const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");
    const PAGE_SIZE = 20;
    
    // ===== Installer Loader =====
    const loadInstallerOptions: LoadOptions<InstallerOptionAsync, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;

            try {
                const params = new URLSearchParams({
                    signageFirmID: organizationId || "",
                    licenseType: "SIGNAGE_TEAM_MEMBER",
                    page: String(page),
                    limit: String(PAGE_SIZE)
                });
                const q = (inputValue || "").trim();
                if (q) params.set("search", q);

                const url = `${process.env.REACT_APP_API_URL}/organization-profiles/search?${params.toString()}`;

                // console.log('Loading installer options:', { page, q, signageFirmID, url });

                const response = await fetch(url, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    // console.error("Failed to fetch installers:", response.statusText);
                    return { options: [], hasMore: false, additional: { page } };
                }

                const json = await response.json();
                // console.log('Installer API response:', { page, q, json });

                const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

                const options: InstallerOptionAsync[] = list.map((u: any) => ({
                    value: normalizeId(u),
                    label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
                    firstName: u?.firstName || "",
                    lastName: u?.lastName || "",
                    email: u?.email || "",
                    phone: u?.phone1 || "",
                }));

                const hasMore = Array.isArray(json)
                    ? options.length === PAGE_SIZE
                    : json?.pagination?.more ??
                        (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (err) {
                console.error("Failed to load installer options:", err);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [organizationId, token]
    );

    const navigate = useNavigate();

    // Function to clear all filters
    const handleClearFilters = () => {
        setDatePreset(DATE_OPTIONS[2]); // Last 30 Days
        setCustomFrom("");
        setCustomTo("");
        setSelectedInstallers([]);
        setSelectedInstallerOptions([]);
        setSelectedStatuses(["Completed"]); // Default to Completed
        setGlobalFilter("");
        setSearchInput("");
        setPagination({ pageIndex: 0, pageSize: 10 });
        fetchInstallationOrder();
    };

    const hasShownToast = useRef(false);
    useEffect(() => {
        if (location.state?.successMessage && !hasShownToast.current) {
            toast.success(location.state.successMessage);
            hasShownToast.current = true;
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    // Removed fetchInstallers useEffect - now using AsyncPaginate

    // useEffect(() => {
    //     const fetchInstallationOrder = async () => {
    //         try {
    //             const res = await fetch(`${process.env.REACT_APP_API_URL}/reports/signage/installer_jobs/datatable`, {
    //                 method: 'POST',
    //                 headers: {
    //                     Authorization: `Bearer ${token}`,
    //                     "Content-Type": "application/json",
    //                 },
    //                 body: JSON.stringify({
    //                     page: pagination.pageIndex + 1,
    //                     limit: pagination.pageSize,
    //                     search: globalFilter,
    //                     installer_membership_ids: selectedInstallers,
    //                     statuses: selectedStatuses,
    //                     date_range: {
    //                         type: datePreset.value,
    //                         from: datePreset.value === "custom" ? customFrom : "",
    //                         to: datePreset.value === "custom" ? customTo : "",
    //                     },
    //                     sort: {
    //                         field: "created_at",
    //                         order: "desc"
    //                     },
    //                     // fields: ["firstName", "lastName", "email", "jobTitle", "warehouses", "status", "created_at"]
    //                 })
    //             });

    //             const result = await res.json();
    //             console.log('Fetched team members:', result);

    //             const teamData = result?.data ?? [];
    //             setRowCount(result?.total ?? 0);

    //             if (Array.isArray(teamData)) {
    //                 setData(result?.data || []);
    //                 setFiltered(teamData);
    //             } else {
    //                 setData(result?.data || []);
    //                 setFiltered([]);
    //             }
    //         } catch (err) {
    //             toast.error("Failed to fetch team members.");
    //             setData([]);
    //             setFiltered([]);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     fetchInstallationOrder();
    // }, [pagination, globalFilter, selectedInstallers, selectedStatuses, datePreset, deleting]);

    // ✅ Make fetch function outside useEffect
    const fetchInstallationOrder = async () => {
        try {
            setLoading(true);
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/reports/signage/installer_jobs/datatable`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        page: pagination.pageIndex + 1,
                        limit: pagination.pageSize,
                        search: globalFilter,
                        installer_membership_ids: selectedInstallers,
                        statuses: selectedStatuses,
                        date_range: {
                            type: datePreset.value,
                            from: datePreset.value === "custom" ? customFrom : "",
                            to: datePreset.value === "custom" ? customTo : "",
                        },
                        sort: {
                            field: "created_at",
                            order: "desc",
                        },
                    }),
                }
            );

            const result = await res.json();
            console.log("Fetched team members:", result);

            const teamData = result?.data ?? [];
            setCards(result?.cards ?? []);
            setRowCount(result?.total ?? 0);

            if (Array.isArray(teamData)) {
                setData(teamData);
                setFiltered(teamData);
            } else {
                setData([]);
                setFiltered([]);
            }
        } catch (err) {
            toast.error("Failed to fetch team members.");
            setData([]);
            setFiltered([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInstallationOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination, globalFilter, deleting]);



    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/warehouses`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                const data = await response.json();
                setWarehouses(Array.isArray(data) ? data : []);
                // console.log('Fetched warehouses:', data);
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
            }
        };

        fetchWarehouses();
    }, [token]);

    // Search and filter logic
    // useEffect(() => {
    //     let filteredData = [...teammembers];
    //     if (searchQuery) {
    //         filteredData = filteredData.filter((m) =>
    //             `${m.firstName} ${m.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
    //         );
    //     }

    //     // if (warehouseFilter) {
    //     //   filteredData = filteredData.filter(
    //     //     (m) => Array.isArray(m.warehouses) && m.warehouses.some((w) => w.label === warehouseFilter)
    //     //   );
    //     // }

    //     if (statusFilter) {
    //         filteredData = filteredData.filter((m) => m.status === statusFilter);
    //     }

    //     setFiltered(filteredData);
    // }, [searchQuery, warehouseFilter, roleFilter, statusFilter, teammembers]);

    const warehouseLabelMap = useMemo(() => {
        const map = new Map();
        if (Array.isArray(warehouses)) {
            for (const w of warehouses) {
                if (w._id && w.label) map.set(w._id, w.label);
            }
        }
        return map;
    }, [warehouses]);

    // Define columns (memoized for TanStack)
    const columns = useMemo(
        () => [
            {
                header: () => <div className="th-text S-No">S.No.</div>,
                id: "sno",
                cell: (info: any) => (
                    <div className="text-sm S-No">
                        {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
                    </div>
                ),
            },
            {
                header: () => <div className="th-text">Installer Name</div>,
                accessorKey: "installer_name",
                cell: (info: any) => (
                    <div className="fw-medium text-sm">{info.getValue() || "--"}</div>
                ),
            },
            {
                header: () => <div className="th-text">Jobs Completed</div>,
                accessorKey: "jobs_completed",
                cell: (info: any) => (
                    <div className="text-sm">{info.getValue() ?? 0}</div>
                ),
            },
            {
                header: () => <div className="th-text">Installations</div>,
                accessorKey: "installations",
                cell: (info: any) => (
                    <div className="text-sm">{info.getValue() ?? 0}</div>
                ),
            },
            {
                header: () => <div className="th-text">Removals</div>,
                accessorKey: "removals",
                cell: (info: any) => (
                    <div className="text-sm">{info.getValue() ?? 0}</div>
                ),
            },
            {
                header: () => <div className="th-text">On-Time %</div>,
                accessorKey: "on_time_percent",
                cell: (info: any) => {
                    const raw = info.getValue();                 // e.g., "92%"
                    const num = typeof raw === "number"
                        ? raw
                        : parseFloat(String(raw ?? "").replace(/[^\d.]/g, "")); // -> 92

                    // Decide label & colors
                    let label = "Needs Improvement";
                    let bg = "#ffe2e2";
                    let fg = "#9f0712";

                    if (Number.isFinite(num)) {
                        if (num > 90) {
                            label = "Excellent";
                            bg = "#dcfce7";
                            fg = "#016630";
                        } else if (num >= 75) {
                            label = "Good";
                            bg = "#fef9c2";
                            fg = "#894b00";
                        }
                    }

                    return (
                        <div className="d-flex align-items-center gap-2">
                            <span>{Number.isFinite(num) ? `${num}%` : "--"}</span>
                            {Number.isFinite(num) && (
                                <span
                                    className="px-2 py-1 rounded-2 fw-medium"
                                    style={{
                                        background: bg,
                                        color: fg,
                                        fontSize: 12,
                                        lineHeight: 1.2,
                                        display: "inline-block",
                                    }}
                                >
                                    {label}
                                </span>
                            )}
                        </div>
                    );
                },
            },

            {
                header: () => <div className="th-text">Installation Jobs with Photos</div>,
                accessorKey: "installation_jobs_with_photos",
                cell: (info: any) => (
                    <div className="text-sm">{info.getValue() ?? 0}</div>
                ),
            },
            {
                header: () => <div className="th-text">Last Job Date</div>,
                accessorKey: "last_job_date",
                cell: (info: any) => (
                    <div className="text-sm">{info.getValue() ?? "--"}</div>
                ),
            },
        ],
        [pagination]
    );



    useEffect(() => {
        const rows: ExportRow[] = filteredTeammembers.map(member => ({
            "First Name": member.firstName || '',
            "Last Name": member.lastName || '',
            "Email": member.email || '',
            "Phone": member.phone || '',
            "Status": member.status ? 'Active' : 'Inactive',
        }));

        setExportRows(rows);
    }, [filteredTeammembers]);


    const table = useReactTable({
        data: data || [],
        columns,
        pageCount: Math.ceil(rowCount / pagination.pageSize),
        state: {
            pagination,
            globalFilter,
        },
        manualPagination: true,
        manualFiltering: true,
        onPaginationChange: setPagination,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
    });

    const pageCount = table.getPageCount();
    const currentPage = table.getState().pagination.pageIndex;

    useEffect(() => {
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [pagination.pageIndex]);

    return (

        <div className="content" ref={topRef}>
            <div className="mb-4">
                <h4 className="mb-1 po-title">Jobs Completed</h4>
                <p className="po-subtitle">Track installer performance and job completion metrics</p>
            </div>
            <div className="g-3 justify-content-lg-center mb-4 row">
                <div className="col-lg-2">
                    <StatCard
                        label={`Total Jobs`}
                        value={cards?.total_jobs ?? 0}
                        money={false}
                        accent="blue"
                        icon="ri-checkbox-circle-line"
                    />
                </div>

                <div className="col-lg-2">
                    <StatCard
                        label="Installations"
                        value={cards?.installations ?? 0}
                        money={false}
                        accent="green"
                        icon="ri-tools-line"
                    />
                </div>

                <div className="col-lg-2">
                    <StatCard
                        label="Removals"
                        value={cards?.removals ?? 0}
                        money={false}
                        accent="red"
                        icon="ri-indeterminate-circle-line"
                    />
                </div>

                <div className="col-lg-2">
                    <StatCard
                        label="Avg On-Time %"
                        value={cards?.avg_on_time_percent ?? 0}
                        money={false}
                        accent="orange"
                        icon="ri-time-line"
                    />
                </div>
                <div className="col-lg-2">
                    <StatCard
                        label="Active Installers"
                        value={cards?.active_installers ?? 0}
                        money={false}
                        accent="purple"
                        icon="ri-group-line"
                    />
                </div>
            </div>

            <div className="po-header mb-4 shadow-border-datatables">
                {/* TOP ROW */}
                <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                    <div className="po-title-wrap me-2">
                        {/* <div>
                            <h4 className="mb-1 po-title">Jobs Completed</h4>
                            <p className="po-subtitle">Track installer performance and job completion metrics</p>
                        </div> */}
                    </div>

                    {/* tools block (search + buttons) */}
                    <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
                        {/* Search */}
                        <div className="input-group po-search ">
                            <span className="input-group-text">
                                <i className="ri-search-line" />
                            </span>
                            <input
                                type="text"
                                id="searchInput"
                                name="searchInput"
                                className="form-control"
                                placeholder="Search by Agent, Office, Brokerage Firm ..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") setGlobalFilter(searchInput); }}
                                autoComplete="off"
                            />
                        </div>

                        {/* Filters toggle */}
                        <button
                            type="button"
                            className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
                            onClick={() => setFiltersOpen(v => !v)}
                            aria-expanded={filtersOpen}
                            aria-controls="incoming-filters"
                        >
                            <i className="ri-filter-3-line me-2" />
                            <span>Filters</span>
                            <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
                        </button>
                    </div>
                </div>

                <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`} style={{ overflow: "visible" }}>
                    <div className="po-filters-bar d-flex" style={{ overflow: "visible" }}> {/* <-- flex wrapper */}
                        <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto" style={{ overflow: "visible" }}>

                            <div className="col-lg-2" style={{ position: "relative" }}>
                                <label className="form-label mb-1 label-1">Date Range</label>
                                <Select
                                    options={DATE_OPTIONS}
                                    value={datePreset}
                                    onChange={val => setDatePreset(val!)}
                                    className="react-select-container"
                                    classNamePrefix="react-select rs"
                                    placeholder="Date Range"
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                />
                            </div>

                            {datePreset.value === "custom" && (
                                <>
                                    <div className="col-lg-2">
                                        <label className="form-label mb-1 label-1">From</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={customFrom}
                                            onChange={e => setCustomFrom(e.target.value)}
                                            min={dateRangeLimits.minDate}
                                            max={dateRangeLimits.maxDate}
                                        />
                                    </div>
                                    <div className="col-lg-2">
                                        <label className="form-label mb-1 label-1">To</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={customTo}
                                            onChange={e => setCustomTo(e.target.value)}
                                            min={dateRangeLimits.minDate}
                                            max={dateRangeLimits.maxDate}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="col-lg-2" style={{ position: "relative" }}>
                                <label className="form-label mb-1 label-1">Installer Name</label>
                                <AsyncPaginate
                                    classNamePrefix="rs"
                                    placeholder="Search installers..."
                                    value={selectedInstallerOptions}
                                    loadOptions={loadInstallerOptions}
                                    onChange={(selected) => {
                                        setSelectedInstallerOptions([...(selected || [])]);
                                        setSelectedInstallers((selected || []).map(o => o.value));
                                    }}
                                    additional={{ page: 1 }}
                                    defaultOptions
                                    debounceTimeout={300}
                                    isMulti
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                />
                            </div>
                            {/* Job Status Filter */}
                            <div className="col-lg-2" style={{ position: "relative" }}>
                                <label className="form-label mb-1 label-1">Job Status</label>
                                <Select
                                    options={JOB_STATUS_OPTIONS}
                                    isMulti
                                    value={JOB_STATUS_OPTIONS.filter(opt => selectedStatuses.includes(opt.value))}
                                    onChange={opts => setSelectedStatuses((opts as any[]).map(o => o.value))}
                                    className="react-select-container"
                                    classNamePrefix="react-select rs"
                                    placeholder="Job Status"
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                />
                            </div>

                            <div className="col-lg-auto d-flex align-items-end gap-2">
                                <button className="btn btn-primary table-addbutton mt-4" onClick={fetchInstallationOrder}>
                                    Apply Filter
                                </button>
                                <button className="btn back-btn surface-btn fw-bold fs-16 mt-4" onClick={handleClearFilters}>
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
                <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
                    <div className="d-flex align-items-center">
                        <i
                            className="ri-file-text-line me-2"
                            aria-hidden="true"
                            style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }}
                        />
                        <h5 className="mb-0">Jobs Completed Data</h5>
                    </div>
                </div>
                <hr className="mb-2" style={{ borderTop: "none" }} />


                {/* DESKTOP/TABLET */}
                <div className="rdt-desktop">
                    <div
                        className="production-scroll"
                        style={{ "--rdt-min": "1200px" } as React.CSSProperties}
                    >
                        <table className="table production-table align-middle mb-0 table-1">
                            {loading ? (
                                <DesktopTableLoader table={table} message="Loading jobs completed…" />
                            ) : (
                                <DesktopTableParts table={table} />
                            )}
                        </table>
                    </div>
                </div>

                {/* MOBILE (≤ 1021px) */}
                <div className="rdt-mobile">
                    {loading ? (
                        <MobileCardsLoader message="Loading jobs completed…" />
                    ) : (
                        <MobileCardsOnly
                        table={table}
                        idColId="order_link"
                        titleColId="product_name"
                        actionColId="actions"
                    />
                    )}
                </div>

            </div>
            <DataTableStatus
                loading={loading}
                isEmpty={table.getRowModel().rows.length === 0}
            >
                <div className="mt-4 mb-4">
                    <TablePaginationControls table={table} />
                </div>
            </DataTableStatus>
        </div>
    );
};

export default JobComplitedReport
